/* ─────────────────────────────────────────────────────────
 * ANIMATION STORYBOARD - Lift & Reveal
 *
 * Read top-to-bottom. Each `at` value is ms after card data loads.
 *
 *    0ms   card data loaded, animation ready
 *   80ms   backdrop blur + dim begins
 *  120ms   card appears (scale 0.96, y: 12px, opacity 0)
 *  200ms   card lifts (y → 0), scale → 1
 *  320ms   slight overshoot (scale 1.01)
 *  420ms   settles to 1.0
 * ───────────────────────────────────────────────────────── */

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import DateTimeSelectionStep from "@/components/booking-shared/DateTimeSelectionStep";
import { getCutoffState } from "@/utils/availabilityCore";
import { BookingCardCrafted, BookingCardPayload } from "@/components/booking-card/BookingCardCrafted";
import { Download } from "lucide-react";
import { formatDate, formatTime } from "@/utils/dateFormatting";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { BookingConfirmationSkeleton } from "@/components/ui/skeleton-block";

type BookingDetail = {
  id: string;
  booking_date: string;
  booking_time: string;
  duration: number;
  status: string | null;
  payment_status: string | null;
  business_id?: string;
  service_id?: string;
  provider_id?: string;
  business_services?: { name: string | null } | null;
  business_profiles?: { name: string | null; booking_url: string | null; phone: string | null; address: string | null; city: string | null; state: string | null; zip: string | null; timezone: string | null } | null;
  service_providers?: { first_name: string | null; last_name: string | null } | null;
};

/* ─────────────────────────────────────────────────────────
 * TIMING - Interface Craft Pattern
 * All values in milliseconds
 * ───────────────────────────────────────────────────────── */
const TIMING = {
  backdropStart: 80,
  cardAppear: 120,
  cardLift: 200,
};

/* ─────────────────────────────────────────────────────────
 * ANIMATION CONFIG - Lift & Reveal
 * ───────────────────────────────────────────────────────── */

// Backdrop - subtle blur and dim
const BACKDROP = {
  initial: { opacity: 0, backdropFilter: 'blur(0px)' },
  animate: { opacity: 1, backdropFilter: 'blur(8px)' },
  transition: { duration: 0.3, ease: 'easeOut' },
};

// Card lift - gentle rise with spring overshoot
const CARD_LIFT = {
  initial: {
    opacity: 0,
    scale: 0.98,
    y: 24
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0
  },
  spring: {
    type: "spring" as const,
    stiffness: 220,
    damping: 24
  },
};

export default function BookingConfirmationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<BookingDetail | null>(null);

  // Manage mode state
  const isManageMode = searchParams.get('mode') === 'manage';
  const [currentAction, setCurrentAction] = useState<'view' | 'reschedule' | 'cancel'>('view');
  const [newDateTime, setNewDateTime] = useState<{ date: Date; time: string } | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cutoffHours, setCutoffHours] = useState(2);
  const [isWithinCutoffWindow, setIsWithinCutoffWindow] = useState(false);
  const [isDataInvalidForReschedule, setIsDataInvalidForReschedule] = useState(false);

  // Booking card state
  const [cardPayload, setCardPayload] = useState<BookingCardPayload | null>(null);
  const [downloadingStoryCard, setDownloadingStoryCard] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    let isMounted = true;

    async function load() {
      try {
        if (!id) { navigate("/"); return; }
        if (isMounted) setLoading(true);

        // Manage mode: load via RPC with link validation
        if (isManageMode) {
          const { data: result, error } = await supabase.rpc('get_booking_details_with_link_validation', {
            p_booking_id: id,
            p_slug: searchParams.get('slug') ?? undefined
          });

          if (error) {
            console.error('RPC error loading booking:', error);
            throw new Error('Failed to load booking details');
          }

          if (!result?.success) {
            console.error('Booking validation failed:', result?.error);
            throw new Error(result?.error || 'Invalid booking link');
          }

          const rpcData = result.data;

          // Transform RPC response to BookingDetail format
          const bookingDetail: BookingDetail = {
            id: rpcData.id,
            booking_date: rpcData.booking_date,
            booking_time: rpcData.booking_time,
            duration: rpcData.service_duration || 60,
            status: rpcData.status,
            payment_status: 'paid',
            business_id: rpcData.business_id,
            service_id: rpcData.service_id,
            provider_id: rpcData.provider_id,
            business_services: { name: rpcData.service_name },
            business_profiles: {
              name: rpcData.business_name,
              booking_url: null,
              phone: rpcData.business_phone,
              address: rpcData.business_address ?? null,
              city: rpcData.business_city ?? null,
              state: rpcData.business_state ?? null,
              zip: rpcData.business_zip ?? null,
              timezone: rpcData.business_timezone ?? null,
            },
            service_providers: rpcData.provider_name ? (() => {
              const parts = (rpcData.provider_name as string).split(' ');
              return { first_name: parts[0] ?? null, last_name: parts.slice(1).join(' ') || null };
            })() : null
          };

          if (isMounted) setDetail(bookingDetail);

          // Fetch booking card payload
          const { data: cardResult, error: cardError } = await supabase.rpc('get_booking_confirmation_card_payload', {
            p_booking_id: id
          });

          if (!cardError && cardResult?.success) {
            if (isMounted) setCardPayload(cardResult.data);
          }

          // Check cutoff window (SHARED UTILITY - used by both modes)
          if (rpcData.business_id && rpcData.booking_date && rpcData.booking_time) {
            const cutoffState = await getCutoffState({
              businessId: rpcData.business_id,
              bookingDate: rpcData.booking_date,
              bookingTime: rpcData.booking_time
            });
            if (isMounted) {
              setCutoffHours(cutoffState.cutoffHours);
              setIsWithinCutoffWindow(cutoffState.withinCutoff);
              setIsDataInvalidForReschedule(false);
            }
          } else if (isMounted) {
            // Missing required data - lock rescheduling for safety
            setIsDataInvalidForReschedule(true);
            setIsWithinCutoffWindow(false);
          }
        } else {
          // Normal mode: use narrow SECURITY DEFINER RPC — no direct anon SELECT on bookings.
          // The old "Public can view their paid bookings" policy was dropped because
          // payment_intent_id is not an authorization boundary and exposed full rows (PII).
          const { data: rpcData, error: rpcError } = await supabase
            .rpc('get_public_booking_confirmation', { p_booking_id: id });

          let bookingDetail: BookingDetail | null = null;

          if (rpcError || !rpcData) {
            // RPC failed or booking not found; fallback to edge function
            const resp = await supabase.functions.invoke('booking-confirmation', { body: { bookingId: id } });
            if ((resp as any).error) throw (resp as any).error;
            bookingDetail = (resp as any).data?.booking || null;
          } else {
            const providerName = rpcData.provider_name as string | null;
            const providerParts = providerName ? providerName.split(' ') : null;
            bookingDetail = {
              id: rpcData.id,
              booking_date: rpcData.booking_date,
              booking_time: rpcData.booking_time,
              duration: rpcData.duration,
              status: rpcData.status,
              payment_status: rpcData.payment_status,
              business_id: rpcData.business_id,
              service_id: rpcData.service_id,
              provider_id: rpcData.provider_id,
              business_services: { name: rpcData.service_name ?? null },
              business_profiles: {
                name: rpcData.business_name ?? null,
                booking_url: rpcData.business_booking_url ?? null,
                phone: rpcData.business_phone ?? null,
                address: rpcData.business_address ?? null,
                city: rpcData.business_city ?? null,
                state: rpcData.business_state ?? null,
                zip: rpcData.business_zip ?? null,
                timezone: rpcData.business_timezone ?? null,
              },
              service_providers: providerParts
                ? { first_name: providerParts[0] ?? null, last_name: providerParts.slice(1).join(' ') || null }
                : null,
            };
          }

          if (isMounted) setDetail(bookingDetail);

          // Fetch booking card payload (normal mode)
          const { data: cardResult, error: cardError } = await supabase.rpc('get_booking_confirmation_card_payload', {
            p_booking_id: id
          });

          if (!cardError && cardResult?.success) {
            if (isMounted) setCardPayload(cardResult.data);
          }

          // Check cutoff window (SHARED UTILITY - used by both modes)
          if (bookingDetail?.business_id && bookingDetail?.booking_date && bookingDetail?.booking_time) {
            const cutoffState = await getCutoffState({
              businessId: bookingDetail.business_id,
              bookingDate: bookingDetail.booking_date,
              bookingTime: bookingDetail.booking_time
            });
            if (isMounted) {
              setCutoffHours(cutoffState.cutoffHours);
              setIsWithinCutoffWindow(cutoffState.withinCutoff);
              setIsDataInvalidForReschedule(false);
            }
          } else if (isMounted) {
            // Missing required data - lock rescheduling for safety
            setIsDataInvalidForReschedule(true);
            setIsWithinCutoffWindow(false);
          }
        }
      } catch (e: any) {
        console.error(e);
        if (isMounted) toast.error(e?.message || 'Failed to load confirmation');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    load();

    return () => {
      isMounted = false;
    };
  }, [id, navigate, isManageMode]);

  const banner = useMemo(() => {
    const status = detail?.payment_status || null;
    if (status === 'paid') return { color: 'bg-green-50 text-green-700', text: 'Payment successful' };
    if (status === 'failed' || status === 'canceled') return { color: 'bg-red-50 text-red-700', text: status === 'failed' ? 'Payment failed' : 'Payment canceled' };
    return { color: 'bg-amber-50 text-amber-800', text: 'Awaiting payment confirmation' };
  }, [detail?.payment_status]);

  const handleReschedule = async () => {
    if (!newDateTime || !detail) {
      toast.error('Please select a new date and time');
      return;
    }

    setRescheduleLoading(true);

    try {
      // Convert 12-hour time to 24-hour format
      const [timePart, period] = newDateTime.time.split(' ');
      const [hours, minutes] = timePart.split(':').map(Number);

      let dbHours = hours;
      if (period === 'PM' && hours !== 12) {
        dbHours += 12;
      } else if (period === 'AM' && hours === 12) {
        dbHours = 0;
      }

      const time24 = `${dbHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;

      // Use Date.UTC to create UTC timestamp directly (no local timezone conversion)
      const year = newDateTime.date.getFullYear();
      const month = newDateTime.date.getMonth();
      const day = newDateTime.date.getDate();
      const newDateTimeUTC = new Date(Date.UTC(year, month, day, dbHours, minutes, 0)).toISOString();

      const newDateISO = newDateTime.date.toISOString().split('T')[0];

      const { rescheduleBooking } = await import('@/services/bookingActions');
      const result = await rescheduleBooking({
        bookingId: detail.id,
        newDatetime: newDateTimeUTC,
        mode: 'customer',
        slug: searchParams.get('slug') ?? undefined,
        context: {
          businessId: detail.business_id,
          serviceId: detail.service_id,
          bookingDate: detail.booking_date,
          bookingTime: detail.booking_time,
        },
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to reschedule appointment');
      }

      toast.success('Appointment rescheduled successfully!');

      setDetail(prev => prev ? {
        ...prev,
        booking_date: newDateISO,
        booking_time: time24.slice(0, 5)
      } : null);

      setCurrentAction('view');
      setNewDateTime(null);
    } catch (err) {
      console.error('Reschedule error:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to reschedule appointment');
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!detail) {
      toast.error('Unable to cancel booking');
      return;
    }

    setCancelLoading(true);

    try {
      const { cancelBooking } = await import('@/services/bookingActions');
      const result = await cancelBooking({
        bookingId: detail.id,
        reason: cancelReason.trim() || 'Cancelled by client',
        mode: 'customer',
        slug: searchParams.get('slug') ?? undefined,
        context: {
          businessId: detail.business_id,
          serviceId: detail.service_id,
          bookingDate: detail.booking_date,
          bookingTime: detail.booking_time,
        },
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to cancel appointment');
      }

      toast.success('Appointment cancelled successfully');

      setDetail(prev => prev ? { ...prev, status: 'cancelled' } : null);
      setCurrentAction('view');
    } catch (err) {
      console.error('Cancel error:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to cancel appointment');
    } finally {
      setCancelLoading(false);
    }
  };

  const addToCalendar = () => {
    if (!detail) return;
    try {
      const pad = (n: number) => String(n).padStart(2, '0');

      // Parse booking_time as "HH:MM:SS" or "HH:MM"
      const [h, m] = detail.booking_time.split(':').map(Number);
      const [yr, mo, dy] = detail.booking_date.split('-').map(Number);
      const durationMinutes = detail.duration || 60;
      const endTotalMinutes = h * 60 + m + durationMinutes;
      const endH = Math.floor(endTotalMinutes / 60) % 24;
      const endM = endTotalMinutes % 60;

      // Format as naive local datetime — no UTC conversion
      const fmtLocal = (year: number, month: number, day: number, hours: number, mins: number) =>
        `${year}${pad(month)}${pad(day)}T${pad(hours)}${pad(mins)}00`;

      // Validate and resolve business timezone with fallback
      const rawTz = detail.business_profiles?.timezone;
      let timezone = 'America/New_York';
      if (rawTz) {
        try { Intl.DateTimeFormat(undefined, { timeZone: rawTz }); timezone = rawTz; } catch {}
      }

      const dtStart = fmtLocal(yr, mo, dy, h, m);
      const dtEnd = fmtLocal(yr, mo, dy, endH, endM);

      // 30-minute reminder
      const reminderMinutes = h * 60 + m - 30;
      const reminderH = Math.floor(reminderMinutes / 60);
      const reminderM = reminderMinutes % 60;
      const dtAlarm = fmtLocal(yr, mo, dy, reminderH, reminderM);

      // Title format: "Booking: [Service] with [Provider First Name or Business Name]"
      const serviceName = detail.business_services?.name || 'Appointment';
      const providerFirstName = detail.service_providers?.first_name;
      const businessName = detail.business_profiles?.name || '';
      const withName = providerFirstName || businessName;
      const summary = withName ? `Booking: ${serviceName} with ${withName}` : `Booking: ${serviceName}`;

      // Location: full business address
      const addressParts = [
        detail.business_profiles?.address,
        detail.business_profiles?.city,
        detail.business_profiles?.state,
        detail.business_profiles?.zip,
      ].filter(Boolean);
      const location = addressParts.join(', ');

      // Description: just the manage link
      const manageLink = window.location.href;
      const description = manageLink;

      const ics = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Figgy//Booking//EN',
        'BEGIN:VEVENT',
        `DTSTART;TZID=${timezone}:${dtStart}`,
        `DTEND;TZID=${timezone}:${dtEnd}`,
        `SUMMARY:${summary}`,
        location ? `LOCATION:${location}` : null,
        `DESCRIPTION:${description}`,
        'BEGIN:VALARM',
        'ACTION:DISPLAY',
        `TRIGGER;TZID=${timezone}:${dtAlarm}`,
        `DESCRIPTION:Reminder: ${summary}`,
        'END:VALARM',
        'END:VEVENT',
        'END:VCALENDAR',
      ].filter(Boolean).join('\n');

      const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'appointment.ics';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {}
  };

  const returnToBusinessHref = useMemo(() => {
    const slug = detail?.business_profiles?.booking_url;
    return slug ? `/${slug}` : '/';
  }, [detail?.business_profiles?.booking_url]);

  const handleDownloadStoryCard = async () => {
    if (!detail?.id) return;

    setDownloadingStoryCard(true);

    try {
      // Step 1: Generate access token (provider auth OR slug validation)
      // In manage mode, read slug from URL (already validated during redirect)
      const slug = isManageMode ? searchParams.get('slug') : null;

      const { data: tokenResult, error: tokenError } = await supabase.rpc('generate_card_access_token', {
        p_booking_id: detail.id,
        p_slug: slug
      });

      if (tokenError || !tokenResult?.success) {
        throw new Error(tokenResult?.error || 'Failed to generate access token');
      }

      const accessToken = tokenResult.token;

      // Step 2: Download story card using access token
      const response = await supabase.functions.invoke('generate-booking-card-image', {
        body: { access_token: accessToken }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to generate card');
      }

      // Response.data should be the image blob
      const imageBlob = response.data;

      // Create download link
      const url = URL.createObjectURL(new Blob([imageBlob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${detail.business_profiles?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'booking'}_card.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Track download for analytics (non-blocking)
      supabase
        .from('booking_card_downloads')
        .insert({
          booking_id: detail.id,
          user_agent: navigator.userAgent,
          ip_address: null // IP will be null from client-side
        })
        .then(({ error }) => {
          if (error) {
            console.warn('Failed to track booking card download:', error);
          }
        });

      toast.success('Story card downloaded successfully!');
    } catch (error: any) {
      console.error('Failed to download story card:', error);
      toast.error(error.message || 'Failed to download story card');
    } finally {
      setDownloadingStoryCard(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <BookingConfirmationSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          {!isManageMode && <div className={`w-full rounded-md px-4 py-3 text-sm font-medium ${banner.color}`}>{banner.text}</div>}
          <CardTitle className="mt-4">Booking Confirmation</CardTitle>
        </CardHeader>
        <CardContent>
          {!detail ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Not found</div>
          ) : (
            <div className="space-y-4">
              {/* Booking Confirmation Card */}
              {cardPayload && (
                prefersReducedMotion ? (
                  <div className="mb-6">
                    <BookingCardCrafted payload={cardPayload} />
                    <div className="flex justify-center mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadStoryCard}
                        disabled={downloadingStoryCard}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {downloadingStoryCard ? 'Generating...' : 'Save for Instagram Story'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <motion.div
                    className="mb-6"
                    initial={CARD_LIFT.initial}
                    animate={CARD_LIFT.animate}
                    transition={{
                      ...CARD_LIFT.spring,
                      delay: TIMING.cardLift / 1000
                    }}
                  >
                    <BookingCardCrafted payload={cardPayload} />
                    <motion.div
                      className="flex justify-center mt-4"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: (TIMING.cardLift + 200) / 1000 }}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadStoryCard}
                        disabled={downloadingStoryCard}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {downloadingStoryCard ? 'Generating...' : 'Save for Instagram Story'}
                      </Button>
                    </motion.div>
                  </motion.div>
                )
              )}

              <div className="flex flex-wrap gap-2 items-center">
                {detail.status && (
                  <Badge variant="secondary" className="capitalize">status: {detail.status}</Badge>
                )}
                {detail.payment_status && (
                  <Badge variant={detail.payment_status === 'paid' ? 'default' : detail.payment_status === 'failed' ? 'destructive' : 'secondary'} className="capitalize">
                    payment: {detail.payment_status}
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Service</div>
                  <div className="font-medium">{detail.business_services?.name || '—'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Provider</div>
                  <div className="font-medium">{detail.service_providers?.first_name ? `${detail.service_providers.first_name} ${detail.service_providers.last_name || ''}`.trim() : detail.business_profiles?.name || '—'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Date</div>
                  <div className="font-medium">{formatDate(detail.booking_date)}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Time</div>
                  <div className="font-medium">{formatTime(detail.booking_time)}</div>
                </div>
                {detail.business_profiles?.address && (
                  <div className="sm:col-span-2">
                    <div className="text-sm text-muted-foreground">Address</div>
                    <div className="font-medium">{detail.business_profiles.address}</div>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                {!isManageMode && <Button onClick={addToCalendar}>Add to Calendar</Button>}
                {!isManageMode && <Button variant="outline" onClick={() => navigate(returnToBusinessHref)}>Return to Business</Button>}
                {!isManageMode && (detail.payment_status === 'failed' || detail.payment_status === 'canceled') && (
                  <Button className="ml-auto" onClick={() => navigate(-1)}>Try Again</Button>
                )}

                {isManageMode && currentAction === 'view' && detail.status !== 'cancelled' && detail.status !== 'completed' && (
                  <>
                    <Button
                      onClick={() => setCurrentAction('reschedule')}
                      disabled={isWithinCutoffWindow || isDataInvalidForReschedule}
                      title={
                        isDataInvalidForReschedule
                          ? 'Rescheduling unavailable due to incomplete booking data'
                          : isWithinCutoffWindow
                            ? `Cannot reschedule within ${cutoffHours} hours of appointment`
                            : ''
                      }
                    >
                      Reschedule
                    </Button>
                    <Button variant="destructive" onClick={() => setCurrentAction('cancel')}>Cancel Booking</Button>
                  </>
                )}
              </div>
            </div>
          )}

          {isManageMode && currentAction === 'reschedule' && detail && (
            <div className="mt-6 space-y-4 border-t pt-6">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Reschedule Appointment</h3>
                <Button variant="ghost" size="sm" onClick={() => { setCurrentAction('view'); setNewDateTime(null); }}>
                  Back
                </Button>
              </div>

              {isDataInvalidForReschedule ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-medium text-red-900">Rescheduling Unavailable</p>
                  <p className="text-sm text-red-700 mt-1">
                    Rescheduling is temporarily unavailable due to incomplete booking data. Please refresh the page or contact support if the issue persists.
                  </p>
                  {detail.business_profiles?.phone && (
                    <p className="text-sm text-red-700 mt-2">
                      Phone: {detail.business_profiles.phone}
                    </p>
                  )}
                </div>
              ) : isWithinCutoffWindow ? (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="font-medium text-orange-900">Rescheduling Locked</p>
                  <p className="text-sm text-orange-700 mt-1">
                    Changes are locked within {cutoffHours} hours of your appointment. Please contact the business directly to reschedule.
                  </p>
                  {detail.business_profiles?.phone && (
                    <p className="text-sm text-orange-700 mt-2">
                      Phone: {detail.business_profiles.phone}
                    </p>
                  )}
                </div>
              ) : (
                <DateTimeSelectionStep
                  businessId={detail.business_id}
                  serviceId={detail.service_id || ''}
                  providerId={detail.provider_id}
                  serviceDuration={detail.duration}
                  selectedDateTime={newDateTime}
                  onSelectDateTime={setNewDateTime}
                />
              )}

              {!isWithinCutoffWindow && !isDataInvalidForReschedule && (
                <>
                  {newDateTime && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium">New appointment time:</p>
                      <p className="text-sm">{newDateTime.date.toLocaleDateString()} at {newDateTime.time}</p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button onClick={handleReschedule} disabled={!newDateTime || rescheduleLoading}>
                      {rescheduleLoading ? 'Rescheduling...' : 'Confirm Reschedule'}
                    </Button>
                    <Button variant="outline" onClick={() => { setCurrentAction('view'); setNewDateTime(null); }}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {isManageMode && currentAction === 'cancel' && detail && (
            <div className="mt-6 space-y-4 border-t pt-6">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Cancel Appointment</h3>
                <Button variant="ghost" size="sm" onClick={() => { setCurrentAction('view'); setCancelReason(''); }}>
                  Back
                </Button>
              </div>

              <div>
                <label className="text-sm font-medium">Reason for cancellation (optional)</label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Please let us know why you're cancelling..."
                  className="mt-2"
                />
              </div>

              <div className="flex gap-2">
                <Button variant="destructive" onClick={handleCancel} disabled={cancelLoading}>
                  {cancelLoading ? 'Cancelling...' : 'Confirm Cancellation'}
                </Button>
                <Button variant="outline" onClick={() => { setCurrentAction('view'); setCancelReason(''); }}>
                  Keep Appointment
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


